import { useMemo, useState } from "react";
import moment from "moment";
import { Box, IconButton, Paper, Typography, makeStyles } from "@material-ui/core";
import {
  AvailabilityCalendarEvent,
  Color,
  DragNSelectCalendar,
  ExpandIcon
} from "@bonliva-crm/ui";
import {
  useGetConsultantAvailabilityFormQuery,
  AvailabilityCalendar_AddAvailability,
  AvailabilityCalendar_DeleteAvailability,
  GetConsultantAvailabilityForm
} from "@bonliva-crm/graphql";
import { Overlay } from "@common";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { removeTimezoneOffset } from "@bonliva-crm/common";
import { ColorDescription } from "./components/color-description";
import { ExpandedViewModal } from "./components/expanded-view-modal";
import { mapGetConsultantAvailabilityFormQuery } from "./mapGetConsultantAvailabilityFormQuery";

type Props = {
  consultantId: string;
  onSelectWeeks?: (value: Date[]) => void;
  hideExpandedView?: boolean;
  disabled?: boolean;
  selectableDates?: Date[];
};

const TOTAL_WEEKS = 72;

export const AvailabilityCalendar = (props: Props) => {
  const classes = useStyles({});
  const { showError } = useSnackbarsPipeline();
  const [availability, setAvailability] = useState<Date[]>(null);
  const [showExpandedView, setShowExpandedView] = useState(false);
  const { weeksFrom, weeksTo } = useMemo(() => {
    return {
      weeksFrom: new Date(),
      weeksTo: moment.utc().add(TOTAL_WEEKS, "weeks").toDate()
    };
  }, []);

  const query = useGetConsultantAvailabilityFormQuery({
    variables: {
      id: props.consultantId,
      weeksFrom: weeksFrom,
      weeksTo: weeksTo
    },
    skip: !props.consultantId,
    onCompleted: (data) => {
      setPreSelectedDates(mapGetConsultantAvailabilityFormQuery(data));
      setAvailability(data.consultantById.Availability.weeks.map((x) => new Date(x)));
    },
    onError: (error) => {
      console.error(error);
      showError(locale.FailedGettingAvailability);
    },
    fetchPolicy: "network-only"
  });

  const [preSelectedDates, setPreSelectedDates] = useState<AvailabilityCalendarEvent[]>(
    []
  );

  const onDateSelection = (weeks: Date[]) => {
    const weeksUtc = weeks.map((x) => removeTimezoneOffset(x));

    if (props.onSelectWeeks) {
      props.onSelectWeeks(weeksUtc);
      return;
    }

    const promises = [];

    const removedWeeks = weeksUtc.filter((x) =>
      availability.some((date) => date.valueOf() === x.valueOf())
    );

    const addedWeeks = weeksUtc.filter(
      (x) => !removedWeeks.some((date) => date.valueOf() === x.valueOf())
    );

    if (addedWeeks.length > 0) {
      promises.push(
        AvailabilityCalendar_AddAvailability.func(
          {
            consultantId: props.consultantId,
            input: { weeks: addedWeeks }
          },
          {
            refetchQueries: [GetConsultantAvailabilityForm.OperationName]
          }
        )
      );
    }

    if (removedWeeks.length > 0) {
      promises.push(
        AvailabilityCalendar_DeleteAvailability.func(
          {
            consultantId: props.consultantId,
            input: { weeks: removedWeeks }
          },
          {
            refetchQueries: [GetConsultantAvailabilityForm.OperationName]
          }
        )
      );
    }

    Promise.all(promises).catch(() => showError(locale.FailedUpdatingAvailability));
  };

  return (
    <Paper className={classes.paper} style={{ position: "relative" }}>
      <Overlay in={props.disabled} normal />

      <Box className={classes.topWrapper}>
        <Typography className={classes.title}>{locale.Availability}</Typography>
        {!props.hideExpandedView && (
          <IconButton onClick={() => setShowExpandedView(!showExpandedView)}>
            <ExpandIcon
              style={{
                marginTop: "4px"
              }}
            />
          </IconButton>
        )}
      </Box>

      <Box className={classes.calendarWrapper}>
        <DragNSelectCalendar
          keepSelected={!!props.onSelectWeeks}
          upcomingWorkplaces={query?.data?.consultantUpcomingWorkplaces ?? []}
          preSelectedDates={preSelectedDates}
          selectableDates={props.selectableDates}
          onSelect={onDateSelection}
        />
        <ColorDescription />
      </Box>

      <ExpandedViewModal
        consultantId={props.consultantId}
        open={showExpandedView}
        onClose={() => setShowExpandedView(false)}
        onSelect={onDateSelection}
      />
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  paper: {
    marginBottom: "1rem",
    height: "fit-content",
    paddingBottom: "1rem",
    width: "100%",
    boxShadow: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column"
  },
  topWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "90%",
    padding: "8 0"
  },
  title: {
    textTransform: "uppercase",
    color: Color.DetailsInput
  },
  calendarWrapper: {
    width: "90%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "2px"
  }
}));
